import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Plus, X, Search, Check, Minus, ChevronDown } from 'lucide-react';
import { AppLayout } from '../../../components/layout';
import { PageBackLink } from '../../../components/layout/PageBackLink';
import { LoadingState, ui } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../../../components/ui/select';
import {
  CHECKLIST_WHO_TO_CHECK,
  searchProductServices,
  searchComplaintsByProduct,
  getExistingWhoToCheckForComplaint,
  fetchChecklistTemplateItems,
  fetchAllChecklistItems,
  fetchChecklistItemById,
  createChecklistItem,
  deleteChecklistItem,
  updateChecklistItem,
  buildChecklistItemPayload } from
'../../../services/fsmService';

const LIST_PATH = '/fsm/complaints-checklist';

let stepUid = 0;
function createStepRow(overrides = {}) {
  stepUid += 1;
  return {
    localId: `step-${stepUid}`,
    checklistId: null,
    whatToCheck: '',
    sequenceOrder: 1,
    isMandatory: false,
    isActive: true,
    originalWhatToCheck: '',
    originalSequenceOrder: 1,
    originalIsMandatory: false,
    originalIsActive: true,
    ...overrides
  };
}

function stepsFromItems(items = []) {
  return items
  .slice()
  .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
  .map((item, idx) =>
  createStepRow({
    checklistId: item.checklistId ?? item.id ?? null,
    whatToCheck: item.whatToCheck || '',
    sequenceOrder: item.sequenceOrder || idx + 1,
    isMandatory: item.isMandatory === true,
    isActive: item.isActive !== false,
    originalWhatToCheck: item.whatToCheck || '',
    originalSequenceOrder: item.sequenceOrder || idx + 1,
    originalIsMandatory: item.isMandatory === true,
    originalIsActive: item.isActive !== false
  })
  );
}

function isStepDirty(step) {
  if (!step.checklistId) return Boolean(String(step.whatToCheck || '').trim());
  return (
    String(step.whatToCheck || '').trim() !== String(step.originalWhatToCheck || '').trim() ||
    Number(step.sequenceOrder) !== Number(step.originalSequenceOrder) ||
    Boolean(step.isMandatory) !== Boolean(step.originalIsMandatory) ||
    Boolean(step.isActive) !== Boolean(step.originalIsActive));

}

export function FsmChecklistTemplateFormPage() {
  const { id: routeItemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const [isTemplateEdit, setIsTemplateEdit] = useState(
    () => prefill.mode === 'edit' || Boolean(routeItemId)
  );

  const [checklistType, setChecklistType] = useState(
    () => prefill.whoToCheck || 'Field Service'
  );
  const [selectedSystem, setSelectedSystem] = useState(() =>
  prefill.productServiceId != null ?
  {
    id: String(prefill.productServiceId),
    productServiceId: prefill.productServiceId,
    name: prefill.productName || `System #${prefill.productServiceId}`
  } :
  null
  );
  const [selectedComplaint, setSelectedComplaint] = useState(() =>
  prefill.complaintTypeId != null ?
  {
    id: String(prefill.complaintTypeId),
    complaintTypeId: prefill.complaintTypeId,
    name: prefill.complaintTypeName || `Complaint #${prefill.complaintTypeId}`
  } :
  null
  );
  const [steps, setSteps] = useState(() => {
    if (
    (prefill.mode === 'edit' || routeItemId) &&
    Array.isArray(prefill.checklistItems) &&
    prefill.checklistItems.length > 0)
    {
      return stepsFromItems(prefill.checklistItems);
    }
    return [createStepRow()];
  });
  const [removedChecklistIds, setRemovedChecklistIds] = useState([]);

  const [systems, setSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);

  const [existingWhoToCheck, setExistingWhoToCheck] = useState(() =>
  prefill.mode === 'edit' && prefill.whoToCheck ? [prefill.whoToCheck] : []
  );
  const [existingItemsLoading, setExistingItemsLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(
    () =>
    Boolean(routeItemId) ||
    (prefill.mode === 'edit' &&
    !(Array.isArray(prefill.checklistItems) && prefill.checklistItems.length > 0))
  );

  const [isSystemOpen, setIsSystemOpen] = useState(false);
  const [systemSearch, setSystemSearch] = useState('');
  const systemDropdownRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const editWhoRef = useRef(prefill.whoToCheck || null);

  const lockedMeta = isTemplateEdit;

  // Deep-link: /edit/:checklistId → resolve template (complaintType + whoToCheck)
  useEffect(() => {
    if (!routeItemId || prefill.mode === 'edit') return;
    let cancelled = false;

    async function loadFromItem() {
      setTemplateLoading(true);
      try {
        const found = await fetchChecklistItemById(routeItemId);
        if (cancelled) return;
        if (!found) {
          setToast({ type: 'error', message: 'Checklist item not found.' });
          setTemplateLoading(false);
          return;
        }

        setIsTemplateEdit(true);
        setChecklistType(found.whoToCheck || 'Field Service');
        editWhoRef.current = found.whoToCheck || 'Field Service';
        if (found.complaintTypeId != null) {
          setSelectedComplaint({
            id: String(found.complaintTypeId),
            complaintTypeId: found.complaintTypeId,
            name: found.complaintTypeName || `Complaint #${found.complaintTypeId}`
          });
        }
        if (found.productServiceId != null) {
          setSelectedSystem({
            id: String(found.productServiceId),
            productServiceId: found.productServiceId,
            name:
            found.systemName && found.systemName !== '—' ?
            found.systemName :
            `System #${found.productServiceId}`
          });
        }
      } catch (err) {
        console.error('Failed to load checklist item:', err);
        if (!cancelled) {
          setToast({
            type: 'error',
            message: err.message || 'Failed to load checklist template.'
          });
          setTemplateLoading(false);
        }
      }
    }

    loadFromItem();
    return () => {
      cancelled = true;
    };
  }, [routeItemId, prefill.mode]);

  // Load systems
  useEffect(() => {
    let cancelled = false;
    setSystemsLoading(true);
    searchProductServices({ page: 0, size: 100, sortBy: 'productServiceId', sortDir: 'ASC' })
    .then((list) => {
      if (!cancelled) setSystems(list);
    })
    .catch((err) => {
      console.error('Failed to load systems:', err);
      if (!cancelled) {
        setSystems([]);
        setToast({ type: 'error', message: err.message || 'Failed to load systems.' });
      }
    })
    .finally(() => {
      if (!cancelled) setSystemsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load complaints when system changes
  useEffect(() => {
    if (!selectedSystem?.productServiceId && !selectedSystem?.id) {
      setComplaints([]);
      return;
    }
    let cancelled = false;
    setComplaintsLoading(true);
    searchComplaintsByProduct({
      productServiceId: selectedSystem.productServiceId ?? selectedSystem.id,
      page: 0,
      size: 100,
      sortBy: 'complaintTypeId',
      sortDir: 'ASC'
    })
    .then((list) => {
      if (!cancelled) setComplaints(list);
    })
    .catch((err) => {
      console.error('Failed to load complaint types:', err);
      if (!cancelled) {
        setComplaints([]);
        setToast({ type: 'error', message: err.message || 'Failed to load complaint types.' });
      }
    })
    .finally(() => {
      if (!cancelled) setComplaintsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedSystem]);

  // Sync system / complaint names once option lists load
  useEffect(() => {
    if (!selectedSystem?.productServiceId || systems.length === 0) return;
    const match = systems.find(
      (s) => String(s.productServiceId ?? s.id) === String(selectedSystem.productServiceId)
    );
    if (match && match.name !== selectedSystem.name) {
      setSelectedSystem(match);
    }
  }, [systems, selectedSystem]);

  useEffect(() => {
    if (!selectedComplaint?.complaintTypeId || complaints.length === 0) return;
    const match = complaints.find(
      (c) => String(c.complaintTypeId ?? c.id) === String(selectedComplaint.complaintTypeId)
    );
    if (match) setSelectedComplaint(match);
  }, [complaints, selectedComplaint?.complaintTypeId]);

  // When complaint type is selected:
  // - Create: for each whoToCheck, check whether THIS complaint type already
  //   has items for that pair (complaintTypeId + whoToCheck) — disable taken ones
  // - Edit: load items for the selected complaintTypeId + whoToCheck only
  useEffect(() => {
    const complaintTypeId = selectedComplaint?.complaintTypeId ?? selectedComplaint?.id;
    if (!complaintTypeId) {
      setExistingWhoToCheck([]);
      return;
    }

    let cancelled = false;
    setExistingItemsLoading(true);

    async function loadForComplaint() {
      try {
        // Taken checklist types = templates that already exist for THIS complaint type
        const existingWho = await getExistingWhoToCheckForComplaint(complaintTypeId);
        if (cancelled) return;
        setExistingWhoToCheck(existingWho);

        if (isTemplateEdit) {
          const who =
          editWhoRef.current || checklistType || 'Field Service';
          const items = await fetchChecklistTemplateItems(complaintTypeId, who);
          if (cancelled) return;
          setSteps(items.length > 0 ? stepsFromItems(items) : [createStepRow()]);
          setRemovedChecklistIds([]);
          setTemplateLoading(false);
        } else {
          const taken = new Set(existingWho.map((w) => w.toLowerCase()));
          if (taken.has(String(checklistType).toLowerCase())) {
            const available = CHECKLIST_WHO_TO_CHECK.find(
              (opt) => !taken.has(opt.toLowerCase())
            );
            if (available) setChecklistType(available);
          }
        }
      } catch (err) {
        console.error('Failed to validate checklist templates for complaint type:', err);
        if (!cancelled) {
          setExistingWhoToCheck([]);
          setToast({
            type: 'error',
            message: err.message || 'Failed to load existing checklist templates.'
          });
          setTemplateLoading(false);
        }
      } finally {
        if (!cancelled) setExistingItemsLoading(false);
      }
    }

    loadForComplaint();
    return () => {
      cancelled = true;
    };
    // checklistType intentionally omitted on create to avoid refetch loops when auto-switching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComplaint?.complaintTypeId, selectedComplaint?.id, isTemplateEdit]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (systemDropdownRef.current && !systemDropdownRef.current.contains(event.target)) {
        setIsSystemOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const takenWhoToCheck = useMemo(() => {
    const taken = new Set(
      existingWhoToCheck.map((w) => String(w).trim().toLowerCase())
    );
    // In edit mode the current template's whoToCheck is allowed
    if (isTemplateEdit && checklistType) {
      taken.delete(String(checklistType).trim().toLowerCase());
    }
    return taken;
  }, [existingWhoToCheck, isTemplateEdit, checklistType]);

  const allTypesTaken =
  !isTemplateEdit &&
  CHECKLIST_WHO_TO_CHECK.every((opt) =>
  takenWhoToCheck.has(opt.toLowerCase())
  );

  const handleSystemChange = (sys) => {
    if (lockedMeta) return;
    setSelectedSystem(sys);
    setSelectedComplaint(null);
    setExistingWhoToCheck([]);
    setSteps([createStepRow()]);
    setRemovedChecklistIds([]);
  };

  const handleComplaintChange = (val) => {
    if (lockedMeta) return;
    const match = complaints.find((c) => String(c.id) === String(val));
    setSelectedComplaint(match || null);
    setSteps([createStepRow()]);
    setRemovedChecklistIds([]);
  };

  const filteredSystems = systems.filter((s) =>
  String(s.name || '')
  .toLowerCase()
  .includes(systemSearch.toLowerCase())
  );

  const handleAddStepRow = () => {
    setSteps((prev) => [...prev, createStepRow({ sequenceOrder: 0 })]);
  };

  const handleRemoveStepRow = (idx) => {
    const target = steps[idx];
    if (target?.checklistId) {
      setRemovedChecklistIds((ids) =>
      ids.includes(target.checklistId) ? ids : [...ids, target.checklistId]
      );
    }
    // Keep surviving items' sequenceOrder values — DB unique key is
    // (complaint_type_id, sequence_order) across all whoToCheck groups.
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [createStepRow({ sequenceOrder: 0 })] : next;
    });
  };

  const handleStepValueChange = (idx, val) => {
    setSteps((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], whatToCheck: val };
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!checklistType) newErrors.checklistType = 'Checklist type is required';
    if (!selectedSystem) newErrors.system = 'System is required';
    if (!selectedComplaint) newErrors.complaintType = 'Type of complaint is required';

    if (!isTemplateEdit && takenWhoToCheck.has(String(checklistType).toLowerCase())) {
      newErrors.checklistType =
      'A template already exists for this complaint type and checklist type.';
    }

    const activeSteps = steps
    .map((s) => String(s.whatToCheck || '').trim())
    .filter(Boolean);
    if (activeSteps.length === 0) {
      newErrors.steps = 'At least one non-empty step particular is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setToast({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }
    if (submitting) return;

    const complaintTypeId = selectedComplaint.complaintTypeId ?? selectedComplaint.id;
    const emptiedExistingIds = [];
    const keptExisting = [];
    const newSteps = [];

    steps.forEach((step) => {
      const text = String(step.whatToCheck || '').trim();
      if (!text) {
        if (step.checklistId) emptiedExistingIds.push(step.checklistId);
        return;
      }
      if (step.checklistId) {
        keptExisting.push({
          ...step,
          whatToCheck: text,
          // Preserve DB sequence — unique across all whoToCheck for this complaint type
          sequenceOrder:
          Number(step.originalSequenceOrder) ||
          Number(step.sequenceOrder) ||
          0
        });
      } else {
        newSteps.push({ ...step, whatToCheck: text });
      }
    });

    setSubmitting(true);
    try {
      const deleteIds = [
      ...new Set([...removedChecklistIds, ...emptiedExistingIds])]
      .map(String);

      for (const id of deleteIds) {
        await deleteChecklistItem(id);
      }

      // Update only rows whose text / flags actually changed (sequence preserved)
      for (const step of keptExisting) {
        if (
        isStepDirty({
          ...step,
          sequenceOrder: step.originalSequenceOrder || step.sequenceOrder
        }))
        {
          await updateChecklistItem(
            step.checklistId,
            buildChecklistItemPayload({
              complaintTypeId,
              whatToCheck: step.whatToCheck,
              whoToCheck: checklistType,
              sequenceOrder: step.sequenceOrder,
              isMandatory: step.isMandatory,
              isActive: step.isActive
            })
          );
        }
      }

      // New rows: allocate sequence after the current max for this complaint type
      if (newSteps.length > 0) {
        const remaining = await fetchAllChecklistItems({ complaintTypeId });
        let nextSeq = remaining.reduce(
          (max, item) => Math.max(max, Number(item.sequenceOrder) || 0),
          0
        );

        for (const step of newSteps) {
          nextSeq += 1;
          await createChecklistItem(
            buildChecklistItemPayload({
              complaintTypeId,
              whatToCheck: step.whatToCheck,
              whoToCheck: checklistType,
              sequenceOrder: nextSeq,
              isMandatory: step.isMandatory,
              isActive: step.isActive
            })
          );
        }
      }

      setToast({
        type: 'success',
        message: isTemplateEdit ?
        'Template successfully updated!' :
        'Template successfully created!'
      });
      setTimeout(() => navigate(LIST_PATH), 900);
    } catch (err) {
      console.error('Failed to save checklist template:', err);
      setToast({
        type: 'error',
        message:
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to save checklist template.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(LIST_PATH);

  const cardCls =
  "bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] border border-(--color-text-border)/60 form-section mb-6 form-surface";
  const labelCls = 'text-[13px] font-semibold text-[#6B7280] mb-1.5 block';
  const inputCls = (hasError) =>
  cn(
    'h-11 px-3 w-full border rounded-xl text-sm bg-white focus:outline-hidden focus:ring-2 focus:ring-accent/15 transition',
    hasError ?
    'border-error focus:border-error' :
    'border-(--color-text-border) focus:border-accent'
  );

  if (templateLoading) {
    return (
      <AppLayout formLayout="standard">
        <LoadingState message="Loading checklist template..." />
      </AppLayout>);

  }

  return (
    <AppLayout formLayout="standard">
      {toast &&
      <div
        className={cn(
          'fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300',
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
        )}>
        
          <span className="text-sm font-semibold">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="hover:opacity-85 text-white/90">
            <X className="h-4 w-4" />
          </button>
        </div>
      }


      <form onSubmit={handleSubmit} className="w-full">
        <div className={cardCls}>
          <div className="mb-5">
            <PageTitleRow back={<PageBackLink to={LIST_PATH} />}>
              <h2 className="text-[16px] font-bold text-primary">
              {isTemplateEdit ? 'Edit Template' : 'Create Template'}
            </h2>
            </PageTitleRow>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 form-grid">
            <div className="flex flex-col">
              <label className={labelCls}><Trans i18nKey="fsm.checklistType2" /></label>
              <Select
                value={checklistType}
                onValueChange={setChecklistType}
                disabled={lockedMeta || existingItemsLoading}>
                
                <SelectTrigger
                  className={cn(
                    'h-11 rounded-xl border-(--color-text-border) bg-white text-sm focus:ring-accent/15 focus:border-accent',
                    errors.checklistType && 'border-error'
                  )}>
                  
                  <SelectValue placeholder={i18n.t("fsm.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  {CHECKLIST_WHO_TO_CHECK.map((opt) => {
                    const disabled = takenWhoToCheck.has(opt.toLowerCase());
                    return (
                      <SelectItem key={opt} value={opt} disabled={disabled}>
                        {opt}
                        {disabled ? ' (exists)' : ''}
                      </SelectItem>);

                  })}
                </SelectContent>
              </Select>
              {errors.checklistType ?
              <p className="text-xs text-error font-medium mt-1">
                  {errors.checklistType}
                </p> :
              allTypesTaken ?
              <p className="text-xs text-amber-600 font-medium mt-1">
                  <Trans i18nKey="fsm.bothChecklistTypesAlreadyHaveATemplateFor" />
                </p> :
              null}
            </div>

            <div className="flex flex-col relative" ref={systemDropdownRef}>
              <label className={labelCls}><Trans i18nKey="fsm.system" /></label>
              <div
                onClick={() => {
                  if (!lockedMeta && !systemsLoading) setIsSystemOpen(!isSystemOpen);
                }}
                className={cn(
                  'h-11 px-3 w-full border rounded-xl text-sm bg-white flex items-center justify-between select-none',
                  lockedMeta ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                  errors.system ? 'border-error' : 'border-(--color-text-border)'
                )}>
                
                <span className={cn('truncate', !selectedSystem && 'text-gray-400')}>
                  {systemsLoading ?
                  'Loading systems...' :
                  selectedSystem?.name || 'Select a system'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
              </div>

              {isSystemOpen && !lockedMeta &&
              <div className="absolute top-[68px] left-0 z-50 w-full bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150 form-surface">
                  <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
                    <Search className="h-4 w-4 text-gray-400 shrink-0" />
                    <input data-page-search="true"
                    type="text"
                    placeholder={i18n.t("formActions.searchSystem")}
                    value={systemSearch}
                    onChange={(e) => setSystemSearch(e.target.value)}
                    className="bg-transparent border-none outline-hidden text-xs w-full h-7 py-1"
                    autoFocus />
                  
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filteredSystems.length === 0 ?
                  <div className="px-4 py-2.5 text-xs text-gray-400 text-center">
                        <Trans i18nKey="fsm.noSystemsFound" />
                      </div> :

                  filteredSystems.map((sys) =>
                  <div
                    key={sys.id}
                    onClick={() => {
                      handleSystemChange(sys);
                      setIsSystemOpen(false);
                      setSystemSearch('');
                    }}
                    className={cn(
                      'px-4 py-2.5 text-xs font-semibold text-foreground hover:bg-workspace cursor-pointer flex items-center justify-between transition-colors',
                      selectedSystem?.id === sys.id &&
                      'bg-workspace text-primary'
                    )}>
                    
                          <span>{sys.name}</span>
                          {selectedSystem?.id === sys.id ?
                    <Check className="h-3.5 w-3.5 text-primary" /> :
                    null}
                        </div>
                  )
                  }
                  </div>
                </div>
              }
              {errors.system ?
              <p className="text-xs text-error font-medium mt-1">{errors.system}</p> :
              null}
            </div>

            <div className="flex flex-col">
              <label className={labelCls}><Trans i18nKey="fsm.typeOfComplaint" /></label>
              <Select
                value={selectedComplaint?.id ?? ''}
                onValueChange={handleComplaintChange}
                disabled={lockedMeta || !selectedSystem || complaintsLoading}>
                
                <SelectTrigger
                  className={cn(
                    'h-11 rounded-xl border-(--color-text-border) bg-white text-sm focus:ring-accent/15 focus:border-accent',
                    errors.complaintType && 'border-error'
                  )}>
                  
                  <SelectValue
                    placeholder={
                    !selectedSystem ?
                    'Choose a system first' :
                    complaintsLoading ?
                    'Loading...' :
                    'Select complaint type'
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  {complaints.map((type) =>
                  <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.complaintType ?
              <p className="text-xs text-error font-medium mt-1">
                  {errors.complaintType}
                </p> :
              null}
            </div>
          </div>
        </div>

        <div className={cardCls}>
          <div className="mb-5">
            <h2 className="text-[16px] font-bold text-primary"><Trans i18nKey="fsm.particulars" /></h2>
          </div>

          {existingItemsLoading && !isTemplateEdit ?
          <p className="text-xs text-gray-400 mb-3"><Trans i18nKey="fsm.checkingExistingTemplates" /></p> :
          null}

          <div className="flex flex-col gap-3 mb-4">
            {steps.map((step, idx) =>
            <div
              key={step.localId}
              className="flex items-center gap-3.5 animate-in fade-in duration-200">
              
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8EEF8] text-[#6B7A99] font-bold text-xs select-none">
                  {idx + 1}
                </span>
                <input
                type="text"
                placeholder={i18n.t("fsm.describeThisStep")}
                value={step.whatToCheck}
                onChange={(e) => handleStepValueChange(idx, e.target.value)}
                className={inputCls()} />
              
                {/* <label className="hidden sm:flex items-center gap-1.5 text-[11px] text-gray-500 shrink-0 cursor-pointer select-none">
                 <input
                   type="checkbox"
                   checked={step.isMandatory}
                   onChange={() => handleStepToggle(idx, 'isMandatory')}
                   className="rounded border-gray-300"
                 />
                 Required
                </label> */}
                <button
                type="button"
                onClick={() => handleRemoveStepRow(idx)}
                className="h-9 w-9 shrink-0 border border-gray-200 hover:bg-rose-50 text-gray-400 hover:text-rose-500 rounded-xl transition cursor-pointer flex items-center justify-center"
                title={i18n.t("formActions.removeRow")}>
                
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleAddStepRow}
            className="h-10 px-4 border border-dashed border-[#C5D0E0] hover:bg-[#F5F8FC] text-sm font-semibold text-primary bg-transparent rounded-xl transition cursor-pointer inline-flex items-center gap-1.5">
            
            <Plus className="h-3.5 w-3.5" /> <Trans i18nKey="formActions.addRow" />
          </button>
          {errors.steps ?
          <p className="text-xs text-error font-semibold mt-3 flex items-center gap-1.5">
              <span>&bull;</span> {errors.steps}
            </p> :
          null}
        </div>

        <div className="flex items-center gap-3 justify-end mb-8 form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
            className="h-10 px-6 rounded-xl border-primary/40 text-primary font-semibold text-sm hover:bg-white bg-white">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <button
            type="submit"
            disabled={submitting || allTypesTaken}
            className={cn(ui.primaryBtn, 'h-10 px-6 disabled:opacity-50')}>
            
            {submitting ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </form>
    </AppLayout>);

}
